import {
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { t } from 'i18next';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useQuery } from 'react-query';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

export default memo(function NewDiscountTable({
  setDiscountList,
  applyDiscount,
  startPrice,
  currency,
  startPricePercentage,
  discount,
  discountPercentage,
  sellPrice,
  priceForMonth,
  month,
  discountId,
  fullPrice,
  setStartPrice,
  setStartPricePercentage,
  setDiscount,
  setDiscountPercentage,
  setSellPrice,
  setPriceForMonth,
  setMonth,
  setPriceForSquare,
  square,
  selectedDiscount,
  toPrint,
  setSelectedDiscount = () => {},
  setStartPriceTopOrder = () => {},
  defaultStartPrice = 0,
  filters = { month: '', payment: '' },
  lastPayment,
}) {
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);

  const { isLoading, isFetching, isError, error, data } = useQuery({
    queryKey: ['/admin/discount/edit/{tabIndex}', discountId],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/admin/discount/edit/${discountId}`,
      );
      return response?.data?.data;
    },
    onSuccess: (res) => {
      const { discounts } = res;
      if (discounts && discounts.length) {
        setDiscountList(discounts);
        const discount = discounts.find(
          (discount) => discount?.payment == defaultStartPrice,
        );

        if (discount) {
          onClick(discount);
        }
      }
    },

    enabled: !hasError && !!discountId && applyDiscount,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  useEffect(() => {
    if (!applyDiscount) {
      setSelectedDiscount({});
    } else if (Object.keys(selectedDiscount)?.length) {
      onClick(selectedDiscount);
    }
  }, [applyDiscount, fullPrice]);

  const discountPriceDis = useCallback(
    (discount) => {
      return (fullPrice * discount) / 100;
    },
    [fullPrice],
  );

  const sellPriceDis = useCallback(
    (discount, payment) => {
      if (data?.type == 1 && payment < 100) {
        if (payment + discount >= 100) return fullPrice;
      }
      return fullPrice * (1 - discount / 100);
    },
    [fullPrice, data?.type],
  );

  const startPriceDis = useCallback(
    (discount, payment) => {
      if (payment >= 100) return fullPrice * (1 - discount / 100);
      if (data?.type == 0) {
        return ((fullPrice - (fullPrice * discount) / 100) * payment) / 100;
      }
      if (data?.type == 1) {
        return (fullPrice * payment) / 100;
      }
    },
    [data?.type, fullPrice],
  );

  const remainingPrice = useCallback(
    (discount, payment) => {
      return sellPriceDis(discount, payment) - startPriceDis(discount, payment);
    },
    [sellPriceDis, startPriceDis],
  );

  const onClick = (discount) => {
    setStartPriceTopOrder(data?.type == 1);
    setSelectedDiscount(discount);
    setStartPrice(startPriceDis(discount?.discount, discount?.payment));
    setStartPricePercentage(discount?.payment);
    setDiscount(discountPriceDis(discount?.discount));
    setDiscountPercentage(discount?.discount);
    setSellPrice(sellPriceDis(discount?.discount, discount?.payment));
    setPriceForMonth(
      discount?.month
        ? (sellPriceDis(discount?.discount, discount?.payment) -
            startPriceDis(discount?.discount, discount?.payment)) /
            discount?.month
        : '0',
    );
    setPriceForSquare(
      sellPriceDis(discount?.discount, discount?.payment) / square,
    );
    setMonth(discount?.month);
  };

  const discounts = useMemo(() => {
    let filtered = data?.discounts;
    if (filters?.month && filters?.payment) {
      filtered = data?.discounts?.filter(
        (dis) =>
          dis?.month == filters?.month && dis?.payment == filters?.payment,
      );
    }
    if (filters?.month) {
      filtered = data?.discounts?.filter((dis) => dis?.month == filters?.month);
    }
    if (filters?.payment) {
      filtered = data?.discounts?.filter(
        (dis) => dis?.payment == filters?.payment,
      );
    }
    if (filtered?.length === 1) {
      onClick(filtered[0]);
    }
    return filtered;
  }, [data, filters]);

  const priceFormMonth = useCallback(
    (remainingPrice, month) => {
      if (remainingPrice < lastPayment) return 0;
      return (remainingPrice - lastPayment) / month;
    },
    [lastPayment],
  );

  return (
    <div className='rounded-2xl shadow-sm p-4  bg-white '>
      <TableContainer className='calculation-table !shadow-none relative z-10 !max-h-[250px]'>
        <Table stickyHeader aria-labelledby='tableTitle'>
          <TableHead>
            <TableRow className='!whitespace-nowrap !bg-[#FFF] [&>*>span]:font-medium'>
              <TableCell>
                <span className='text-sm font-medium text-[#64748B]'>
                  {t('shaxmatka.moreHomeDetails.startPrice')}
                </span>
              </TableCell>
              <TableCell>
                <span className='text-sm font-medium text-[#64748B]'>
                  {t('shaxmatka.moreHomeDetails.discount')}
                </span>
              </TableCell>
              <TableCell>
                <span className='text-sm font-medium text-[#64748B]'>
                  {t('shaxmatka.moreHomeDetails.sellPrice')}
                </span>
              </TableCell>
              <TableCell>
                <span className='text-sm font-medium text-[#64748B]'>
                  {t('shaxmatka.moreHomeDetails.remainingPayment')}
                </span>
              </TableCell>
              <TableCell>
                <span className='text-sm font-medium text-[#64748B]'>
                  {t('shaxmatka.moreHomeDetails.month')}
                </span>
              </TableCell>
              <TableCell>
                <span className='text-sm font-medium text-[#64748B]'>
                  {t('shaxmatka.moreHomeDetails.monthlyPrice')}
                </span>
              </TableCell>
              {!!lastPayment && (
                <TableCell className='!px-2'>
                  <span className='text-small'>
                    {t('common.fields.lastPayment')}
                  </span>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody className='overflow-hidden'>
            {applyDiscount ? (
              isLoading || isFetching ? (
                <TableRow>
                  <TableCell
                    colSpan={lastPayment ? 7 : 6}
                    sx={{ padding: 0, paddingBottom: '50px' }}
                  >
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={lastPayment ? 7 : 6}>
                    <div className='flex flex-col items-center'>
                      {error?.response?.data?.message && (
                        <span className='text-red-600 font-medium'>
                          {error?.response?.data?.message}
                        </span>
                      )}
                      {error?.response?.data?.details &&
                        error?.response?.data?.details[0]?.message && (
                          <div>
                            <span className='text-red-600 font-medium'>
                              {t('common.errors.message')}
                            </span>
                            <span>
                              {error?.response?.data?.details[0]?.message}
                            </span>
                          </div>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : discounts?.length > 0 ? (
                discounts?.map((item) => (
                  <TableRow
                    key={item?.id}
                    hover
                    tabIndex={-1}
                    onClick={() => onClick(item)}
                  >
                    <TableCell
                      className={`!whitespace-nowrap !border-0 !font-medium cell-text ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-600'
                          : ''
                      }`}
                    >
                      <NumericFormat
                        value={startPriceDis(item?.discount, item?.payment)}
                        allowNegative={false}
                        displayType={'text'}
                        thousandSeparator={' '}
                        decimalScale={1}
                        className='w-full text-right'
                        suffix={currency == '1' ? '-$ ' : '-UZS '}
                      />
                      &nbsp;({item?.payment}%)
                    </TableCell>
                    <TableCell
                      className={`!whitespace-nowrap !font-medium !border-0 cell-text ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-600'
                          : ''
                      }`}
                    >
                      <NumericFormat
                        value={discountPriceDis(item?.discount)}
                        allowNegative={false}
                        displayType={'text'}
                        thousandSeparator={' '}
                        decimalScale={1}
                        className='w-full text-right'
                        suffix={currency == '1' ? '-$ ' : '-UZS '}
                      />
                      &nbsp;({item?.discount}%)
                    </TableCell>
                    <TableCell
                      className={`!whitespace-nowrap !font-medium !border-0 cell-text ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-600'
                          : ''
                      }`}
                    >
                      <NumericFormat
                        value={sellPriceDis(item?.discount, item?.payment)}
                        allowNegative={false}
                        displayType={'text'}
                        thousandSeparator={' '}
                        decimalScale={1}
                        className='w-full text-right'
                        suffix={currency == '1' ? '-$' : '-UZS'}
                      />
                    </TableCell>
                    <TableCell
                      className={`!whitespace-nowrap !font-medium !border-0 cell-text ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-600'
                          : ''
                      }`}
                    >
                      <NumericFormat
                        value={remainingPrice(
                          item?.discount,
                          item?.payment,
                          item?.startPrice,
                        )}
                        allowNegative={false}
                        displayType={'text'}
                        thousandSeparator={' '}
                        decimalScale={1}
                        className='w-full text-right'
                        suffix={currency == '1' ? '-$' : '-UZS'}
                      />
                    </TableCell>
                    <TableCell
                      className={`!whitespace-nowrap !font-medium !border-0 cell-text ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-600'
                          : ''
                      }`}
                    >
                      {item?.month}
                    </TableCell>
                    <TableCell
                      className={`!whitespace-nowrap !font-medium !border-0 cell-text ${
                        toPrint ? '!py-2 !whitespace-nowrap' : ''
                      } ${
                        selectedDiscount?.id === item?.id
                          ? '!text-green-600'
                          : ''
                      }`}
                    >
                      <NumericFormat
                        value={
                          item?.month
                            ? lastPayment
                              ? priceFormMonth(
                                  remainingPrice(
                                    item?.discount,
                                    item?.payment,
                                    item?.startPrice,
                                  ),
                                  item?.month,
                                )
                              : (sellPriceDis(item?.discount, item?.payment) -
                                  startPriceDis(
                                    item?.discount,
                                    item?.payment,
                                  )) /
                                item?.month
                            : '0'
                        }
                        allowNegative={false}
                        displayType={'text'}
                        thousandSeparator={' '}
                        decimalScale={1}
                        className='w-full text-right text-[13px]'
                        // suffix={currency == '1' ? '-$' : '-UZS'}
                        suffix=''
                      />
                    </TableCell>
                    {!!lastPayment ? (
                      <TableCell
                        className={`!whitespace-nowrap !font-medium !border-0 cell-text ${
                          toPrint ? '!py-2 !whitespace-nowrap' : ''
                        } ${
                          selectedDiscount?.id === item?.id
                            ? '!text-green-600'
                            : ''
                        }`}
                      >
                        {item?.payment == 100 ? (
                          '0'
                        ) : (
                          <NumericFormat
                            value={
                              lastPayment >
                              remainingPrice(
                                item?.discount,
                                item?.payment,
                                item?.startPrice,
                              )
                                ? remainingPrice(
                                    item?.discount,
                                    item?.payment,
                                    item?.startPrice,
                                  )
                                : lastPayment
                            }
                            allowNegative={false}
                            displayType={'text'}
                            thousandSeparator={' '}
                            decimalScale={1}
                            className='w-full text-right text-[13px]'
                            suffix=''
                          />
                        )}
                      </TableCell>
                    ) : (
                      <></>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <span className='no-data-found-wrapper'>
                      <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                      {t('common.global.noDataFound')}
                    </span>
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableRow hover tabIndex={-1}>
                <TableCell
                  className={`cell-text !font-medium !border-0 !text-green-600 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  <NumericFormat
                    value={startPrice}
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-green-600 text-right'
                    suffix={currency == '1' ? '-$ ' : '-UZS '}
                  />
                  &nbsp;({startPricePercentage}%)
                </TableCell>
                <TableCell
                  className={`cell-text !font-medium !border-0 !text-green-600 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  <NumericFormat
                    value={discount}
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-green-600 text-right'
                    suffix={currency == '1' ? '-$' : '-UZS'}
                  />
                  &nbsp;({discountPercentage}%)
                </TableCell>
                <TableCell
                  className={`cell-text !font-medium !border-0 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  <NumericFormat
                    value={sellPrice}
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-green-600 text-right'
                    suffix={currency == '1' ? '-$' : '-UZS'}
                  />
                </TableCell>
                <TableCell
                  className={`cell-text !font-medium !border-0 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  <NumericFormat
                    value={sellPrice - startPrice}
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-green-600 text-right'
                    suffix={currency == '1' ? '-$' : '-UZS'}
                  />
                </TableCell>
                <TableCell
                  className={`cell-text !font-medium !border-0 !text-green-600 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  {month}
                </TableCell>
                <TableCell
                  className={`cell-text !font-medium !border-0 ${
                    toPrint ? '!py-2 !whitespace-nowrap' : ''
                  }`}
                >
                  <NumericFormat
                    value={priceForMonth}
                    allowNegative={false}
                    displayType={'text'}
                    thousandSeparator={' '}
                    decimalScale={1}
                    className='w-full text-green-600 text-right'
                    suffix={currency == '1' ? '-$' : '-UZS'}
                  />
                </TableCell>
                {!!lastPayment && (
                  <TableCell
                    className={`cell-text !font-medium !border-0 ${
                      toPrint ? '!py-2 !whitespace-nowrap' : ''
                    }`}
                  >
                    <NumericFormat
                      value={lastPayment}
                      allowNegative={false}
                      displayType={'text'}
                      thousandSeparator={' '}
                      decimalScale={1}
                      className='w-full text-green-600 text-right'
                      suffix={currency == '1' ? '-$' : '-UZS'}
                    />
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});
